/**
 * Turns the two ways wallet provisioning can fail into something a user can act
 * on.
 *
 * A synchronous failure throws out of the ceremony. A detached failure never
 * throws at all: the HyperEVM creation is started, the backend runs it out of
 * band, and a failure lands the queue row back on `admitted` carrying
 * `lastError`/`lastErrorCode`. Both have to reach the same visible state with a
 * working action behind it, so both funnel through here.
 */

import type { OnboardingQueueState } from '@/lib/api/onboarding';
import {
    describeErrorForUser,
    isApiError,
    isGenericUserError,
    isUserRejection,
    userErrorText,
} from '@/lib/errors';

/** What the user can do about it. */
export type ProvisioningFailureKind =
    /** Re-run the ceremony: the admission is still live, so a retry re-claims it. */
    | 'retry'
    /** The admission is gone: the queue has to be rejoined before signing again. */
    | 'rejoin';

export interface ProvisioningFailure {
    kind: ProvisioningFailureKind;
    /** Headline shown to the user. */
    title: string;
    /** Secondary sentence naming the underlying reason, when there is one. */
    reason: string | null;
}

const EXPIRED_ADMISSION: ProvisioningFailure = {
    kind: 'rejoin',
    title: 'Your setup window expired.',
    reason: 'Your voucher is still valid. Rejoin the line to finish setup.',
};

const DETACHED_TITLE =
    'Wallet setup hit a problem on our side. You can retry now, your progress is saved.';

/**
 * The wallet changed accounts mid-ceremony: the statements were signed by the
 * account the wallet is live on, while the app token they were posted under
 * belongs to the one that signed in. The preflight catches this before any
 * signature, so anything that still reaches here is a switch that happened
 * inside the ceremony itself.
 */
const OWNER_CHANGED_TITLE =
    "Your wallet's active account changed during setup.";
const OWNER_CHANGED_ACTION =
    'Switch back to the account you signed in with, or sign out and start again with this one.';

/**
 * Friendly text per backend error code. Anything unmapped falls back to the
 * sentence the backend wrote, which is already user-facing.
 */
const REASON_BY_CODE: Record<string, string> = {
    proofExpired: 'Your signature expired. Please sign again.',
    signerNotRegistered:
        'We could not verify your wallet with the signing service.',
    signerStatusUnavailable:
        'We could not reach the signing service. Please try again in a moment.',
    signerUnavailable:
        'The signing service is temporarily unavailable. Please try again in a moment.',
    signerRejected:
        'The signing service rejected the request. Try again, and contact support if it keeps happening.',
    serviceWalletUnavailable:
        'Our wallet service is busy right now. Please try again in a moment.',
    custodialProvisioningUnavailable:
        'Wallet creation is temporarily unavailable. Please try again in a moment.',
    ownerAuthorizationRequired: `${OWNER_CHANGED_TITLE} ${OWNER_CHANGED_ACTION}`,
};

/** The `code` an API/business error carries, if any. */
export function provisioningErrorCode(error: unknown): string | null {
    if (isApiError(error)) return error.body?.code ?? null;
    if (typeof error === 'object' && error !== null && 'code' in error) {
        const code = (error as { code?: unknown }).code;
        return typeof code === 'string' && code ? code : null;
    }
    return null;
}

/** Friendly sentence for a backend error code, falling back to its message. */
export function provisioningReason(
    code: string | null | undefined,
    fallback: string | null | undefined,
): string | null {
    if (code && REASON_BY_CODE[code]) return REASON_BY_CODE[code];
    return fallback?.trim() ? fallback : null;
}

/** Maps a throw out of the provisioning ceremony onto a recoverable failure. */
export function failureFromThrown(error: unknown): ProvisioningFailure {
    if (isUserRejection(error)) {
        return {
            kind: 'retry',
            title: 'Signature cancelled.',
            reason: 'Start again and approve the requests in your wallet.',
        };
    }
    const code = provisioningErrorCode(error);
    if (code === 'voucherRequired') return EXPIRED_ADMISSION;
    if (code === 'ownerAuthorizationRequired') {
        // retrying re-runs the preflight, which answers with the mismatch panel
        // and its two real actions rather than another dead signature.
        return {
            kind: 'retry',
            title: OWNER_CHANGED_TITLE,
            reason: OWNER_CHANGED_ACTION,
        };
    }
    if (code === 'proofExpired') {
        return {
            kind: 'retry',
            title: REASON_BY_CODE.proofExpired,
            reason: null,
        };
    }
    // never the raw throw: a zod dump or a revert string would land straight in
    // the panel headline. an error the presenter cannot place keeps this flow's
    // own wording, which says the progress is saved.
    const described = describeErrorForUser(error, {
        operation: 'Wallet setup',
    });
    return {
        kind: 'retry',
        title:
            provisioningReason(
                code,
                isGenericUserError(described) ? null : userErrorText(described),
            ) ?? DETACHED_TITLE,
        reason: null,
    };
}

/**
 * Reads a failure out of the queue status while a detached creation is being
 * awaited. Returns null while the run is still healthy.
 *
 * `admitted` with a `lastError` is the exact shape the incident produced: the
 * detached run gave the lane back instead of finishing it.
 */
export function failureFromQueue(status: {
    state: OnboardingQueueState;
    lastError: string | null;
    lastErrorCode?: string | null;
}): ProvisioningFailure | null {
    if (status.state === 'missed') return EXPIRED_ADMISSION;
    if (status.state === 'admitted' && status.lastError) {
        return {
            kind: 'retry',
            title: DETACHED_TITLE,
            reason: provisioningReason(status.lastErrorCode, status.lastError),
        };
    }
    return null;
}

/** Nothing came back at all: never leave the user on a spinner over it. */
export function stalledProvisioningFailure(): ProvisioningFailure {
    return {
        kind: 'retry',
        title: 'Wallet setup is taking longer than expected. You can try again, your progress is saved.',
        reason: null,
    };
}
